import React, { Key, useState } from 'react';
import { useAsyncList } from '@react-stately/data';
import { MenuTriggerAction } from '@react-types/combobox';
import { LoadingState } from '@react-types/shared';
import cn from 'classnames';

import { useBranches } from '../../hooks/useBranches';
import { getBranchOptions } from '../../utils/BranchUtils';
import {
  getBranchIdFromBranchName,
  getBranchNameFromBranchId
} from '../../utils/ComboBoxUtils';

import { ComboBox } from '../ComboboxWithInitialValue';
import {
  AsyncListLoadFunction,
  Branch,
  OptionsType,
  OverlayOptions
} from '../types';

export interface BranchComboBoxProps {
  label: string;
  options?: Array<OptionsType>;
  onSelectionChange?: (e: Key) => void;
  selectedKey?: string;
  placeholder?: string;
  renderListItem?: any;
  onBlur?: (e: React.FocusEvent) => void;
  onOpenChange?: (
    isOpen: boolean,
    menuTrigger?: MenuTriggerAction | undefined
  ) => void;
  error?: string;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  defaultFilter?: any;
  isDisabled?: boolean;
  apiStatus?: string;
  autoFocus?: boolean;
  listItemStyles?: string;
  itemContentStyles?: string;
  loadingState?: LoadingState;
  onLoadMore?: () => any;
  overlayOptions?: OverlayOptions;
  getListItemTextValue?: (T: any) => any;
  initialBranchValue?: string;
  mobilePlaceholder?: string;
  enableDataMask?: boolean;
}

export const BranchesComboBox = (props: BranchComboBoxProps) => {
  const {
    onOpenChange,
    onSelectionChange,
    selectedKey,
    inputFieldStyles = '',
    buttonStyles = '',
    placeholder = '',
    error,
    initialBranchValue,
    mobilePlaceholder,
    enableDataMask,
    ...others
  } = props;
  const [previousFilteredText, setPreviousFilteredText] = useState(undefined);

  const { branches } = useBranches();

  const onSelectionChangeFunc = (key) => {
    if (key) {
      onSelectionChange && onSelectionChange(key);
    }
  };

  const onOpenChangeFunc = (key: any) => {
    onOpenChange && onOpenChange(key);
  };

  const onInputChangeFunc = (value) => {
    setPreviousFilteredText(value);
    if (value === '') {
      onSelectionChange('');
    }
  };

  const loadItems: AsyncListLoadFunction<
    { branch_id: string; branch_name: any },
    any
  > = async ({ signal, cursor, filterText, selectedKeys, items }) => {
    let previousBranches = branches.current ? branches.current : [];
    if (previousBranches && previousBranches.length > 0) {
      previousBranches = previousBranches.filter(
        (eachBranch) => eachBranch.branch_id !== 'Other'
      );
    }

    let json = { branches: [...previousBranches] };
    if (previousFilteredText !== filterText) {
      json = { branches: getBranchOptions() };
      if (json.branches && json.branches.length > 0) {
        const excludeBranches = [...json.branches];

        if (filterText) {
          json.branches = excludeBranches.filter((eachInstitute) =>
            eachInstitute.branch_name
              .toLowerCase()
              .includes(filterText.toLowerCase())
          );
        }
      }
    }

    const otherOption = filterText
      ? [
          {
            branch_id: 'Other',
            branch_name: filterText
          }
        ]
      : [];

    const branchItems = [...json.branches, ...otherOption];

    branches.current = branchItems;

    return {
      items: branchItems,
      cursor: null,
      filterText: filterText
    };
  };

  const list = useAsyncList({
    load: loadItems,
    initialFilterText: initialBranchValue ?? '',
    initialSelectedKeys: [],
    getKey: (item: Branch) => item.branch_id
  });

  const getInputTextFromValue = (value, list) => {
    return getBranchNameFromBranchId(value, list);
  };

  const getValueFromInputText = (text, list) => {
    return getBranchIdFromBranchName(text, list);
  };

  return (
    <ComboBox
      {...others}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChangeFunc}
      onInputChange={onInputChangeFunc}
      onOpenChange={onOpenChangeFunc}
      inputFieldStyles={cn('w-full', inputFieldStyles)}
      buttonStyles={buttonStyles}
      getListItemKey={(item) => item.branch_id}
      placeholder={placeholder}
      getListItemTextValue={(item) => item.branch_name}
      renderListItem={({ item }) => {
        let branchName = item.branch_name;
        if (item.branch_id === 'Other' && !!branchName) {
          branchName = `Create Option "${branchName}"`;
        }
        return <p key={item.branch_id}>{branchName}</p>;
      }}
      error={error}
      getInputTextFromValue={getInputTextFromValue}
      getValueFromInputText={getValueFromInputText}
      initialValue={initialBranchValue}
      list={list}
      getDisplayValueFromSelectedState={(item) => {
        return item.branch_name;
      }}
      mobilePlaceholder={mobilePlaceholder}
      enableDataMask={enableDataMask}
    />
  );
};
